import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  MessageSquare, Mail, Clock, User, Send, CheckCircle,
  AlertCircle, Inbox, RefreshCw, Trash2, Eye, Reply,
  Bug, Lightbulb, CreditCard, Handshake, HelpCircle, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  pending: { label: 'Bekliyor', color: 'bg-amber-500/20 text-amber-400', icon: Clock },
  in_progress: { label: 'İşlemde', color: 'bg-blue-500/20 text-blue-400', icon: RefreshCw },
  replied: { label: 'Yanıtlandı', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  closed: { label: 'Kapatıldı', color: 'bg-slate-500/20 text-slate-400', icon: CheckCircle }
};

const CATEGORY_MAP = {
  general: { label: 'Genel', icon: HelpCircle, color: 'text-blue-400' },
  bug: { label: 'Hata', icon: Bug, color: 'text-red-400' },
  feature: { label: 'Özellik', icon: Lightbulb, color: 'text-amber-400' },
  billing: { label: 'Ödeme', icon: CreditCard, color: 'text-green-400' },
  partnership: { label: 'Ortaklık', icon: Handshake, color: 'text-purple-400' }
};

const AdminContact = () => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [requestsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/contact/admin/list`, { headers }),
        axios.get(`${API_URL}/api/contact/admin/stats`, { headers })
      ]);
      
      setRequests(requestsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/contact/admin/${requestId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Durum güncellendi');
      fetchData();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) {
      toast.error('Yanıt boş olamaz');
      return;
    }

    setReplying(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/contact/admin/${selectedRequest.id}/reply`,
        { reply: replyText, status: 'replied' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Yanıt kaydedildi');
      setReplyDialogOpen(false);
      setReplyText('');
      setSelectedRequest(null);
      fetchData();
    } catch (error) {
      toast.error('Yanıt gönderilemedi');
    } finally {
      setReplying(false);
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Bu talebi silmek istediğinize emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/contact/admin/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Talep silindi');
      fetchData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const filteredRequests = requests.filter(r => {
    if (filter === 'all') return true;
    return r.status === filter;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <MessageSquare className="h-6 w-6 text-primary" />
              İletişim Talepleri
            </h1>
            <p className="text-sm text-slate-400 mt-1">Kullanıcı mesajlarını yönetin</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Yenile
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="border-slate-700/50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-slate-400">Toplam</p>
              </CardContent>
            </Card>
            <Card className="border-amber-500/30">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-400">{stats.pending}</p>
                <p className="text-xs text-slate-400">Bekliyor</p>
              </CardContent>
            </Card>
            <Card className="border-blue-500/30">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-400">{stats.in_progress}</p>
                <p className="text-xs text-slate-400">İşlemde</p>
              </CardContent>
            </Card>
            <Card className="border-green-500/30">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-400">{stats.replied}</p>
                <p className="text-xs text-slate-400">Yanıtlandı</p>
              </CardContent>
            </Card>
            <Card className="border-slate-500/30">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-slate-400">{stats.closed}</p>
                <p className="text-xs text-slate-400">Kapatıldı</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-400" />
          {['all', 'pending', 'in_progress', 'replied', 'closed'].map(f => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? 'default' : 'ghost'}
              onClick={() => setFilter(f)}
              className="text-xs"
            >
              {f === 'all' ? 'Tümü' : STATUS_MAP[f]?.label}
            </Button>
          ))}
        </div>

        {/* Requests List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-slate-400">Yükleniyor...</p>
          </div>
        ) : filteredRequests.length === 0 ? (
          <Card className="border-dashed border-slate-700">
            <CardContent className="py-12 text-center">
              <Inbox className="h-12 w-12 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-400">Henüz talep yok</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredRequests.map(request => {
              const status = STATUS_MAP[request.status] || STATUS_MAP.pending;
              const category = CATEGORY_MAP[request.category] || CATEGORY_MAP.general;
              const StatusIcon = status.icon;
              const CategoryIcon = category.icon;

              return (
                <Card key={request.id} className="border-slate-700/50 hover:border-slate-600 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      {/* Left: Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Badge className={status.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {status.label}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            <CategoryIcon className={`h-3 w-3 mr-1 ${category.color}`} />
                            {category.label}
                          </Badge>
                          <span className="text-xs text-slate-500">#{request.id.slice(0, 8).toUpperCase()}</span>
                        </div>
                        
                        <h3 className="font-semibold mb-1 truncate">{request.subject}</h3>
                        <p className="text-sm text-slate-400 line-clamp-2 mb-2">{request.message}</p>
                        
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {request.name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {request.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(request.created_at)}
                          </span>
                        </div>

                        {/* Admin Reply Preview */}
                        {request.admin_reply && (
                          <div className="mt-3 p-2 bg-green-500/10 border border-green-500/20 rounded text-xs">
                            <span className="text-green-400 font-medium">Yanıt:</span>
                            <span className="text-slate-300 ml-2">{request.admin_reply.slice(0, 100)}...</span>
                          </div>
                        )}
                      </div>

                      {/* Right: Actions */}
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedRequest(request);
                            setReplyText(request.admin_reply || '');
                            setReplyDialogOpen(true);
                          }}
                          className="h-8"
                        >
                          <Reply className="h-4 w-4" />
                        </Button>
                        {request.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleStatusChange(request.id, 'in_progress')}
                            className="h-8 text-blue-400"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        {request.status !== 'closed' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleStatusChange(request.id, 'closed')}
                            className="h-8 text-slate-400"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(request.id)}
                          className="h-8 text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Reply Dialog */}
        <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
          <DialogContent className="max-w-lg bg-brand-dark border-slate-800">
            <DialogHeader>
              <DialogTitle>Yanıt Yaz</DialogTitle>
            </DialogHeader>
            
            {selectedRequest && (
              <div className="space-y-4">
                {/* Original Message */}
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2 text-sm">
                    <User className="h-4 w-4 text-slate-400" />
                    <span className="font-medium">{selectedRequest.name}</span>
                    <span className="text-slate-500">({selectedRequest.email})</span>
                  </div>
                  <p className="text-sm font-medium mb-1">{selectedRequest.subject}</p>
                  <p className="text-sm text-slate-400">{selectedRequest.message}</p>
                </div>

                {/* Reply Input */}
                <div>
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Yanıtınızı yazın..."
                    className="min-h-[150px] bg-slate-800/50"
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setReplyDialogOpen(false)}>
                İptal
              </Button>
              <Button onClick={handleReply} disabled={replying}>
                {replying ? 'Gönderiliyor...' : 'Yanıtı Kaydet'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
  );
};

export default AdminContact;
